// src/components/healthcare/schemaBuilder/ComponentPalette.tsx

import React from "react";
import { Typography, Tooltip } from "antd";
import { useDraggable } from "@dnd-kit/core";
import {  FontSizeOutlined, NumberOutlined, CheckCircleOutlined, UnorderedListOutlined, OrderedListOutlined, GroupOutlined } from "@ant-design/icons";
import { SchemaFieldType } from "../../types/appointments/SchemaBuilderTypes";

const { Text } = Typography;

interface PaletteItem {
	type: SchemaFieldType;
	label: string;
	description: string;
	icon: React.ReactNode;
	category: "structure" | "field";
}

const PALETTE_ITEMS: PaletteItem[] = [
	{ type: "object", label: "Group", description: "Group related fields together", icon: <GroupOutlined />, category: "structure" },
	{ type: "text", label: "Text", description: "Single or multi-line text", icon: <FontSizeOutlined />, category: "field" },
	{ type: "number", label: "Number", description: "Numeric value", icon: <NumberOutlined />, category: "field" },
	{ type: "boolean", label: "Yes/No", description: "True or false value", icon: <CheckCircleOutlined />, category: "field" },
	{ type: "enum", label: "Choice", description: "Select from options", icon: <UnorderedListOutlined />, category: "field" },
	{ type: "array", label: "List", description: "Multiple items", icon: <OrderedListOutlined />, category: "field" },
];

const DraggablePaletteItem: React.FC<{ item: PaletteItem; onClick: () => void }> = ({ item, onClick }) => {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `palette-${item.type}`,
		data: { type: item.type, label: item.label, icon: item.icon, fromPalette: true },
	});

	const isStructure = item.category === "structure";

	return (
		<Tooltip title={item.description} placement="right">
			<div
				ref={setNodeRef}
				onClick={onClick}
				style={{ opacity: isDragging ? 0 : 1, cursor: "grab" }}
				className={`flex items-center gap-2 px-2 py-1.5 rounded mb-1 transition-colors select-none ${
					isStructure
						? "text-indigo-600 hover:bg-indigo-50"
						: "text-slate-600 hover:bg-teal-50 hover:text-teal-700"
				}`}
				{...attributes}
				{...listeners}
			>
				<span className={`text-sm flex-shrink-0 ${isStructure ? "text-indigo-400" : "text-teal-500"}`}>{item.icon}</span>
				<Text className="text-xs">{item.label}</Text>
			</div>
		</Tooltip>
	);
};

const ComponentPalette: React.FC<{ onAddComponent: (type: SchemaFieldType) => void }> = ({ onAddComponent }) => {
	const structureItems = PALETTE_ITEMS.filter((item) => item.category === "structure");
	const fieldItems = PALETTE_ITEMS.filter((item) => item.category === "field");

	return (
		<div className="component-palette h-full flex flex-col">
			<div className="mb-3">
				<Text strong className="text-xs block">Components</Text>
				<Text type="secondary" className="text-[10px] block mt-0.5">Click or drag to add</Text>
			</div>

			<div className="flex-1 overflow-y-auto">
				<div className="mb-3">
					<Text className="text-[10px] font-semibold uppercase tracking-wide block mb-1 text-indigo-500">Structure</Text>
					{structureItems.map((item) => (
						<DraggablePaletteItem key={item.type} item={item} onClick={() => onAddComponent(item.type)} />
					))}
				</div>

				<div>
					<Text className="text-[10px] font-semibold uppercase tracking-wide block mb-1 text-teal-500">Fields</Text>
					{fieldItems.map((item) => (
						<DraggablePaletteItem key={item.type} item={item} onClick={() => onAddComponent(item.type)} />
					))}
				</div>
			</div>
		</div>
	);
};

export default ComponentPalette;